import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';
import { LayoutDetailPages } from 'hoc/LayoutDetailPages';
import { FormikProvider, useFormik } from 'formik';
import { useMatchMedia } from 'hooks/useMatchMedia';
import { fetchOrderType, updateOrderType } from 'store/orderTypes/actions';

import { NavBar } from 'components/Common/NavBar';
import { useTranslation } from 'react-i18next';
import { ORDERTYPES_BREADCRUMBS } from 'common/data/beadcrumbs';
import { OrderTypeMainCard } from 'components/OrderType/OrderTypeMainCard';
import { OrderTypeCountryCard } from 'components/OrderType/OrderTypeCountryCard';
import { OrderTypeApplicantCard } from 'components/OrderType/OrderTypeApplicantCard';
import { OrderTypeDocumentsCard } from 'components/OrderType/OrderTypeDocumentsCard';
import { OrderTypeCountriesCard } from 'components/OrderType/OrderTypeCountriesCard';

import {
  convertOrderTypeFromApi,
  convertOrderTypeToApi,
} from 'helpers/converters';
import { OrderTypeDatesCard } from 'components/OrderType/OrderTypeDatesCard';
import { HeadControlButtons } from 'components/HeadControllButtons/HeadControllButtons';
import Loader from 'components/Common/Loader';
import { Row, Col } from 'reactstrap';

const formikInitialVals = {
  id: null,
  translationEn: '',
  translationDe: '',
  isActive: true,
  enableForIndividual: true,
  enableForLegal: true,

  enableCountry: true,
  availableForCountries: [],

  applicantMaxQuantity: 1,
  enableApplicantBirthdate: false,
  enableApplicantPassport: false,

  enableContactPerson: true,

  enableResidency: false,

  enableDescription: true,

  enableVisaType: false,
  enableVisaUrgency: false,

  enableDocumentsReceivedBy: true,
  enableDeliveryMethod: true,
  // Dates
  enableDocumentsToConsEstimatedAt: true,
  enableDocumentsToConsActualAt: true,
  enableDocumentsFromConsEstimatedAt: true,
  enableDocumentsFromConsActualAt: true,
  enableDocumentsToApplicantEstimatedAt: true,
  enableDocumentsToApplicantActualAt: true,
  enableFirstDateOfEntry: false,
  enableGultigFrom: false,
  enableGultigTo: false,
  enableGrantedVisa: false,
  countries: [],
};

const getOrderTypeState = state => state.orderTypes.orderTypes.orderType;

const OrderType = () => {
  const { id } = useParams();
  const { isMobile, isTablet } = useMatchMedia();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orderType, isLoading } = useSelector(getOrderTypeState);

  const handleSubmit = values => {
    dispatch(
      updateOrderType({
        orderType: convertOrderTypeToApi(values),
        id,
      }),
    );
  };

  const formik = useFormik({
    initialValues: formikInitialVals,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  // Memoized navigation to prevent unnecessary recalculations
  const orderTypeNav = useMemo(() => {
    let defaultNav = [
      { title: 'orderTypes.main', id: 1, link: 'mainSettings', offset: -110 },
      { title: 'orderTypes.country', id: 2, link: 'countrySettings' },
      { title: 'orderTypes.applicants', id: 3, link: 'applicantsSettings' },
      { title: 'orderTypes.dates', id: 6, link: 'datesSettings' },
    ];

    if (formik.values.enableCountry) {
      defaultNav.push({
        title: 'orderTypes.countries',
        id: 4,
        link: 'countriesSettings',
      });
    } else {
      defaultNav.push({
        title: 'orderTypes.documents',
        id: 5,
        link: 'documentsSettings',
      });
    }

    return defaultNav.sort((a, b) => a.id - b.id);
  }, [formik.values.enableCountry]);

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderType(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (orderType) {
      formik.resetForm({
        values: { ...convertOrderTypeFromApi(orderType) },
      });
    }
  }, [orderType]);

  // Responsive extra content
  const renderExtraContent = () => (
    <div
      className={classNames(
        'd-flex w-100',
        isMobile
          ? 'justify-content-start flex-wrap gap-2'
          : 'justify-content-end',
      )}
      style={{ gap: isMobile ? '8px' : '0' }}
    >
      <HeadControlButtons
        typeCancel="gray"
        handleSubmit={formik.handleSubmit}
        path="/orderTypes"
        isDisabledButton={!formik.dirty || isLoading}
      />
    </div>
  );

  // Responsive cards rendering
  const renderCards = () => {
    const cardProps = {
      className: isMobile ? 'mb-3' : 'mb-4',
    };

    return (
      <>
        <Col xs={12} className="px-0">
          <OrderTypeMainCard {...cardProps} />
        </Col>

        <Col xs={12} className="px-0">
          <OrderTypeCountryCard {...cardProps} />
        </Col>

        <Col xs={12} className="px-0">
          <OrderTypeApplicantCard {...cardProps} />
        </Col>

        {formik.values.enableCountry && (
          <Col xs={12} className="px-0">
            <OrderTypeCountriesCard orderType={orderType} {...cardProps} />
          </Col>
        )}

        {!formik.values.enableCountry && (
          <Col xs={12} className="px-0">
            <OrderTypeDocumentsCard orderType={orderType} {...cardProps} />
          </Col>
        )}

        <Col xs={12} className="px-0">
          <OrderTypeDatesCard {...cardProps} />
        </Col>
      </>
    );
  };

  return (
    <FormikProvider value={formik}>
      <LayoutDetailPages
        layoutConfig={{
          title: t('orderTypes.orderType'),
          isScrollableHeader: true,
          breadcrumbs: ORDERTYPES_BREADCRUMBS,
          extraContent: renderExtraContent(),
        }}
        navBarContent={
          <NavBar
            scrollableHeader
            components={orderTypeNav}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        }
      >
        {isLoading ? (
          <Row
            className="justify-content-center align-items-center"
            style={{ minHeight: '200px' }}
          >
            <Loader isLoading={isLoading} />
          </Row>
        ) : (
          <Row className="g-3 g-xl-4">{renderCards()}</Row>
        )}
      </LayoutDetailPages>
    </FormikProvider>
  );
};

export default OrderType;
